import type { SqlStatement, StorageAdapter } from "../adapter/adapter.js";
import { labelsAlters, labelsTable } from "./labels.js";
import { recurrenceAlters, recurrenceTable } from "./recurrence.js";
import { recurrenceOverridesAlters, recurrenceOverridesIndexes, recurrenceOverridesTable } from "./recurrence-overrides.js";
import { calendarsAlters, calendarsTable } from "./calendars.js";
import { settingsTable } from "./settings.js";
import { tasksAlters, tasksIndexes, tasksTable } from "./tasks.js";
import { themesAlters, themesTable } from "./themes.js";
import { topicsAlters, topicsIndexes, topicsTable } from "./topics.js";

/** Run all migrations in order. Idempotent — safe to call on every startup. */
export async function migrate(db: StorageAdapter): Promise<void> {
  await db.execute(tasksTable);
  for (const idx of tasksIndexes) await db.execute(idx);
  for (const stmt of tasksAlters) {
    // ADD COLUMN errors on existing column; treat as no-op so the migration is idempotent.
    try { await db.execute(stmt); } catch { /* column already present */ }
  }
  await db.execute(labelsTable);
  for (const stmt of labelsAlters) {
    try { await db.execute(stmt); } catch { /* column already present */ }
  }
  await db.execute(topicsTable);
  for (const idx of topicsIndexes) await db.execute(idx);
  for (const stmt of topicsAlters) {
    try { await db.execute(stmt); } catch { /* column already present */ }
  }
  await db.execute(calendarsTable);
  for (const stmt of calendarsAlters) {
    try { await db.execute(stmt); } catch { /* column already present */ }
  }
  await db.execute(recurrenceTable);
  for (const stmt of recurrenceAlters) {
    try { await db.execute(stmt); } catch { /* column already present */ }
  }
  await db.execute(recurrenceOverridesTable);
  for (const idx of recurrenceOverridesIndexes) await db.execute(idx);
  for (const stmt of recurrenceOverridesAlters) {
    try { await db.execute(stmt); } catch { /* column already present */ }
  }
  await db.execute(settingsTable);
  await db.execute(themesTable);
  for (const stmt of themesAlters) {
    try { await db.execute(stmt); } catch { /* column already present */ }
  }
  await backfillMutColumns(db);
}

/** Marker key for {@link backfillMutColumns}. Lives in `settings`, which is why
 *  this runs after the settings table exists. */
const MUT_BACKFILL_KEY = "migration_mut_backfill";

/**
 * ONE-TIME: seed the device-local `mut` / `*_acked_mut` change-tracking columns
 * on rows that predate them (see core `sync/device-local.ts`).
 *
 * - `mut = 1`, `server_acked_mut = 0`: every existing row reads as
 *   server-dirty once, so the first sync after the upgrade re-pushes the full
 *   graph. Harmless by design — the server's merge no-ops identical rows
 *   (`recordsEqual`, no new seq/rev) — and it is what retires the old
 *   `uploaded_link_rev` queue: any link the server never learned goes out with
 *   this sweep. (This also supersedes the old `enrollExistingLinksForUpload`
 *   migration, which only served the deleted uploaded_link_rev mechanism.)
 * - `google_acked_mut` is mapped from the OLD mechanism's cleanliness: a row
 *   whose `updated_at` is covered by its calendar's legacy `last_push_at`
 *   cursor was pushed to Google and starts clean (1 = its backfilled mut);
 *   anything else starts dirty (0). Without this mapping every task on a bound
 *   calendar would be re-PATCHed once — hundreds of no-op writes that bump the
 *   etag for every other client of that calendar.
 *
 * Written in TypeScript, not SQL: `MemoryAdapter` (browser) only understands
 * `WHERE <col>=?`, so a bulk conditional UPDATE would silently match nothing on
 * the platform most users are on. Guarded by a settings marker because
 * `migrate()` runs on every startup and re-running would re-dirty every row
 * forever. Uses `executeBatch` when the adapter has it so a native install with
 * tens of thousands of rows doesn't pay one IPC round-trip per row.
 */
async function backfillMutColumns(db: StorageAdapter): Promise<void> {
  const done = await db.select<{ value: string }>(
    `SELECT value FROM settings WHERE key=?`,
    [MUT_BACKFILL_KEY],
  );
  if (done.length > 0) return;

  type Raw = Record<string, unknown>;
  const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

  const calendars = await db.select<Raw>(`SELECT * FROM calendars`);
  const tasks = await db.select<Raw>(`SELECT * FROM tasks`);
  const rules = await db.select<Raw>(`SELECT * FROM recurrence_rules`);
  const overrides = await db.select<Raw>(`SELECT * FROM recurrence_overrides`);
  const labels = await db.select<Raw>(`SELECT * FROM labels`);
  const topics = await db.select<Raw>(`SELECT * FROM topics`);

  // Legacy per-calendar Google push cursor — the old cleanliness authority.
  const pushCursorByCalendar = new Map<string, string>();
  for (const c of calendars) {
    const cursor = str(c["last_push_at"]);
    const id = str(c["id"]);
    if (cursor && id) pushCursorByCalendar.set(id, cursor);
  }
  // A rule/override belongs to Google through the task that renders it into an
  // event body — resolve each to that task's calendar.
  const calendarByRule = new Map<string, string | null>();
  for (const t of tasks) {
    const ruleId = str(t["recurrence_id"]);
    if (ruleId && !calendarByRule.has(ruleId)) calendarByRule.set(ruleId, str(t["calendar_id"]));
  }
  const googleClean = (updatedAt: unknown, calendarId: string | null): boolean => {
    const cursor = calendarId ? pushCursorByCalendar.get(calendarId) : undefined;
    return !!cursor && typeof updatedAt === "string" && updatedAt <= cursor;
  };

  const statements: SqlStatement[] = [];
  const seed = (table: string, id: string | null, google: 0 | 1 | null): void => {
    if (!id) return;
    if (google === null) {
      statements.push({
        sql: `UPDATE ${table} SET mut=?,server_acked_mut=? WHERE id=?`,
        params: [1, 0, id],
      });
    } else {
      statements.push({
        sql: `UPDATE ${table} SET mut=?,server_acked_mut=?,google_acked_mut=? WHERE id=?`,
        params: [1, 0, google, id],
      });
    }
  };

  for (const t of tasks) {
    seed("tasks", str(t["id"]), googleClean(t["updated_at"], str(t["calendar_id"])) ? 1 : 0);
  }
  for (const r of rules) {
    const cal = calendarByRule.get(str(r["id"]) ?? "") ?? null;
    seed("recurrence_rules", str(r["id"]), googleClean(r["updated_at"], cal) ? 1 : 0);
  }
  const ruleOf = new Map(rules.map((r) => [str(r["id"]), r] as const));
  for (const o of overrides) {
    const ruleId = str(o["recurrence_id"]);
    const cal = ruleId && ruleOf.has(ruleId) ? calendarByRule.get(ruleId) ?? null : null;
    seed("recurrence_overrides", str(o["id"]), googleClean(o["updated_at"], cal) ? 1 : 0);
  }
  for (const c of calendars) seed("calendars", str(c["id"]), 0);
  for (const l of labels) seed("labels", str(l["id"]), null);
  for (const t of topics) seed("topics", str(t["id"]), null);

  const BATCH = 500;
  for (let i = 0; i < statements.length; i += BATCH) {
    const chunk = statements.slice(i, i + BATCH);
    if (db.executeBatch) await db.executeBatch(chunk);
    else for (const s of chunk) await db.execute(s.sql, s.params ?? []);
  }

  await db.execute(
    `INSERT INTO settings (key,value) VALUES (?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
    [MUT_BACKFILL_KEY, new Date().toISOString()],
  );
}

export { tasksTable, tasksIndexes, tasksAlters, labelsTable, labelsAlters, topicsTable, topicsIndexes, topicsAlters, calendarsTable, calendarsAlters, recurrenceTable, recurrenceAlters, recurrenceOverridesTable, recurrenceOverridesIndexes, recurrenceOverridesAlters, settingsTable, themesTable, themesAlters };
